import React, { useEffect, FunctionComponent, useState } from 'react';
import { Box, Link, Typography, Checkbox } from '@material-ui/core';
import accessoryPackItemStyles from './AccessoryPackItemStyles';
import clsx from 'clsx';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import AccessoryPackModal from '../accessoryPackModal/AccessoryPackModal';
import ConfigurablePrice from '../../../presentational/prices/configurablePrice/ConfigurablePrice';
import Translation from '../../../../../common/components/translation/Translation';
import usePriceValidation from '../../../../../gqlCommon/hooks/currency/usePriceValidation';
import usePrice from '../../../../../gqlCommon/hooks/prices/usePrice';
import useFeatureChildren from '../../../../hooks/queries/useFeatureChildren';
import { GetFeatureChildren_featureChildren } from '../../../../graphql/queries/__generated__/GetFeatureChildren';
import IPrice from '../../../../../gqlCommon/model/prices/IPrice';
import CategoryFeatureTypes from '../../../../enum/CategoryFeatureTypes';
import useCustomizeCarContext from '../../../../hooks/useCustomizeCarContext';

export interface IAccessoryPackItemProps {
  id: string;
  name: string;
  prices: IPrice[];
  selected: boolean;
  onClick: (id: string) => void;
  onToggle: (id: string) => void;
}

const AccessoryPackItem: FunctionComponent<IAccessoryPackItemProps> = ({
  id,
  selected,
  name,
  prices,
  onClick,
  onToggle,
}) => {
  const styles = accessoryPackItemStyles();
  const {
    platformConfig: { priceDisplay },
  } = usePlatformConfig();
  const { selectedCar } = useCustomizeCarContext();
  const [featureChildren, setFeatureChildren] = useState<GetFeatureChildren_featureChildren[]>([]);
  const { primaryPrice, secondaryPrice } = usePrice(prices);
  const [isModalOpen, setModalOpen] = useState(false);
  const { isMultiCurrency } = usePriceValidation(selectedCar?.prices.carPrice.originalPrice);
  const { data, loading, loadFeatureChildren } = useFeatureChildren();
  const [isChecked, setIsChecked] = useState<boolean>(selected);

  useEffect(() => {
    setIsChecked(selected);
  }, [selected]);

  useEffect(() => {
    if (isModalOpen) {
      loadFeatureChildren({
        featureCode: id,
      });
    }
  }, [id, isModalOpen]);

  useEffect(() => {
    if (!loading && data) {
      setFeatureChildren(data?.featureChildren);
    }
  }, [data]);

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      className={clsx([styles.container, { [styles.packSelected]: isChecked }])}
      data-test={`option:pack:${id}`}
    >
      <Checkbox
        size={'medium'}
        checked={isChecked}
        onChange={() => {
          onClick(id);
          setIsChecked(!isChecked);
        }}
        data-test={'pack:checkbox'}
        className={styles.checkbox}
      />
      <Box className={styles.titlePriceContainer}>
        <Box mr={2}>
          <Typography {...(selected && { 'data-option': `${CategoryFeatureTypes.ACCESSORY_PACK}:selected` })}>
            {name}
          </Typography>
          <Link
            className={styles.priceGuideToggle}
            onClick={() => setModalOpen(true)}
            variant={'helpingText'}
            underline={'always'}
            color={'secondary'}
            data-test={'accessories:pack:showModal'}
          >
            <Translation id="trans__configurator__customise_page_options_section__show_features_link" />
          </Link>
        </Box>
        <Box>
          <ConfigurablePrice
            prices={prices}
            display={{ showOrLabel: true, hidePriceLabel: true }}
            classes={{
              single: { priceText: styles.priceText },
              multi: {
                container: styles.multiCurrencyContainer,
                priceText: styles.multiCurrencyPriceText,
              },
            }}
          />
        </Box>
      </Box>
      <AccessoryPackModal
        id={id}
        name={name}
        primaryPrice={primaryPrice}
        secondaryPrice={secondaryPrice}
        accessories={featureChildren}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        isSelected={selected}
        onToggle={onToggle}
        priceDisplay={priceDisplay}
        loading={loading}
        isMulticurrency={isMultiCurrency}
      />
    </Box>
  );
};

export default AccessoryPackItem;
